import { PlusIcon } from "@radix-ui/react-icons";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { fitComponents } from "components/MessageTemplates/TriggerDialogs";
import { LABEL } from "components/ProfileDropdown/settingsStyles";
import { Select } from "components/Select";
import { MouseEvent, ReactNode } from "react";

/*
 * Small controls the Automations pickers share, in the trigger dialog's
 * look (TriggerDialogs.tsx): labeled rows, compact number boxes, the
 * app's Select, and the periwinkle pills.
 */

/**
 * Every add button on a lane, one look: Add next step, Add a trigger gate,
 * Add a trigger and Add additional action. onAdd gets the button, which
 * gets the focus back when what it opened closes.
 */
export function AddButton({
  label,
  ariaLabel,
  onAdd,
}: {
  label: string;
  /** Says more than the label, when several on a lane read the same. */
  ariaLabel?: string;
  onAdd: (anchor: HTMLElement) => void;
}) {
  return (
    <Button
      type="button"
      color={ButtonColor.GREEN}
      fill={ButtonFill.HOLLOW}
      size={ButtonSize.SMALL}
      extraClasses="whitespace-nowrap"
      aria-label={ariaLabel}
      onClick={(e: MouseEvent<HTMLElement>) => onAdd(e.currentTarget)}
    >
      <PlusIcon aria-hidden /> {label}
    </Button>
  );
}

/** A labeled row: the settings label over its controls, in one wrapping line. */
export function FieldRow({
  label,
  htmlFor,
  children,
  className = "mt-3",
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      {htmlFor ? (
        <label htmlFor={htmlFor} className={LABEL}>
          {label}
        </label>
      ) : (
        <p className={LABEL}>{label}</p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
        {children}
      </div>
    </div>
  );
}

export { default as NumberBox } from "components/Inputs/NumberBox";

export interface Choice<V> {
  value: V;
  label: string;
  /** Under the label in the menu (a kind's description, say). */
  hint?: string;
  isDisabled?: boolean;
  /** A small icon before the label, in the field and in the menu (a step's action). */
  icon?: ReactNode;
}

export interface ChoiceGroup<V> {
  label: string;
  options: Choice<V>[];
}

function flat<V>(options: (Choice<V> | ChoiceGroup<V>)[]): Choice<V>[] {
  return options.flatMap((o) => ("options" in o ? o.options : [o]));
}

/**
 * The app's Select for a value among choices (or groups of choices). The
 * value is found among them (Select opens on an option object), and
 * values compare as text, as the stored ones may be numbers. Its list is
 * as wide as its widest choice, so none wraps onto two lines; a hint
 * wraps under its choice, and never widens the list.
 */
export function ChoiceSelect<V extends string | number | null>({
  label,
  value,
  options,
  onChange,
  placeholder = "Pick one",
  searchable,
  className = "min-w-[12rem] max-w-md",
  inputId,
  disabled,
}: {
  label: string;
  value: V | undefined;
  options: (Choice<V> | ChoiceGroup<V>)[];
  onChange: (value: V) => void;
  placeholder?: string;
  searchable?: boolean;
  className?: string;
  inputId?: string;
  disabled?: boolean;
}) {
  const all = flat(options);
  const chosen = all.find((o) => String(o.value) === String(value)) ?? null;
  return (
    <div className={className}>
      <Select
        inputId={inputId}
        aria-label={label}
        isSearchable={searchable ?? all.length > 10}
        isDisabled={disabled}
        menuPlacement="auto"
        options={options}
        value={chosen}
        placeholder={placeholder}
        onChange={(o: Choice<V>) => o && onChange(o.value)}
        components={fitComponents}
        formatOptionLabel={(o: Choice<V>, meta: { context: string }) => {
          const label = o.icon ? (
            <span className="flex items-center gap-2">
              {o.icon}
              {o.label}
            </span>
          ) : (
            o.label
          );
          return meta.context === "menu" && o.hint ? (
            <span className="min-w-0 flex-1">
              <span className="block text-sm">{label}</span>
              <span className="block w-0 min-w-full text-xs text-gray-500">
                {o.hint}
              </span>
            </span>
          ) : (
            label
          );
        }}
        noOptionsMessage={() => "Nothing matches"}
      />
    </div>
  );
}

export interface PillOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Why it is disabled, on hover. */
  title?: string;
}

/**
 * A choice of a few, as Tadpole's periwinkle pills: "xs" as the trigger
 * dialog's Who gets it, "sm" as the approval queue's Show.
 */
export function PillChoice<T extends string>({
  label,
  value,
  onChange,
  options,
  size = "xs",
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: PillOption<T>[];
  size?: "xs" | "sm";
}) {
  return (
    <div
      className={`inline-flex w-fit flex-wrap rounded-full border border-[#98acfc] ${
        size === "sm" ? "p-1" : "p-0.5"
      }`}
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={option.disabled}
          title={option.title}
          className={`rounded-full px-3 py-1 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
            size === "sm" ? "text-sm" : "text-xs"
          } ${
            value === option.value
              ? "bg-[#98acfc] text-white"
              : "text-[#98acfc] hover:bg-[#98acfc]/10"
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
